/**
 * @module ActionableMessageHttpChannelAdapter
 */

import {
    HttpChannelAdapter,
    ActivityRequest,
    ActivityResponse,
    ActivityStatus,
} from 'adaptivecards-v2';

import { MessageContext, CardDetails } from 'owa-actionable-message-v2';
import { getUserConfiguration } from 'owa-session-store';
import { getClientAuthToken } from 'owa-connectors';

/**
 * @enum
 * @property {string} Completed - A property represents a completed state
 * @property {string} Failed - A property represents a failed state
 */

enum RequestStatus {
    Completed = 'Completed',
    Failed = 'Failed',
}

/**
 * @class ActionableMessageHttpChannelAdapter
 * A class to represent structure of http channel adapter for Actionable Messages
 * @implements HttpChannelAdapter
 */

export default class ActionableMessageHttpChannelAdapter extends HttpChannelAdapter {
    /**
     * @private
     * A member to hold the card details
     */

    private cardDetails: CardDetails;

    /**
     * @private
     * A member to hold the message context object
     */

    private messageContext: MessageContext;

    /**
     * @private
     * A member to hold a callback on card details updated
     */

    private onCardDetailsUpdated: (card: object) => void;

    /**
     * @public
     * @constructor
     * To create an instance of ActionableMessageHttpChannelAdapter
     * @param {string} url - A request url string
     * @param {CardDetails} cardDetails - A card details object
     * @param {MessageContext} messageContext - A message context object
     * @param {(card: object) => void} onCardDetailsUpdated - A callback when card details updated
     * @returns An instance of ActionableMessageHttpChannelAdapter
     */

    public constructor(
        url: string,
        cardDetails: CardDetails,
        messageContext: MessageContext,
        onCardDetailsUpdated: (card: object) => void
    ) {
        super(url);
        this.cardDetails = cardDetails;
        this.messageContext = messageContext;
        this.onCardDetailsUpdated = onCardDetailsUpdated;
    }

    /**
     * @public
     * @async
     * To send out the http action execute request
     * @param {ActivityRequest} request - A request object for action execute
     * @returns A promise that resolves to an Activity response
     */

    public async sendRequestAsync(request: ActivityRequest): Promise<ActivityResponse> {
        try {
            const channelData = {
                connectorSenderGuid: this.cardDetails.connectorSenderGuid,
                /**
                 * TODO: Fill in input params
                 */
                inputParameters: [],
                senderAddress: this.messageContext.senderAddress,
                SmtpAddressesSerialized: this.cardDetails.smtpAddressesSerialized,
                adaptiveCardSignature: this.cardDetails.cardSignature,
                adaptiveCardHash: this.cardDetails.cardHash,
                /**
                 * TODO: Fill in client telemetry object
                 */
                clientTelemetry: {},
            };

            request.activity.channelData = channelData;

            const userConfiguration = getUserConfiguration();
            const userPrincipalName = userConfiguration.SessionSettings?.UserPrincipalName;
            const userEmail = userConfiguration.SessionSettings?.UserEmailAddress;

            request.activity.from = {
                id: userPrincipalName ? userPrincipalName : userEmail,
            };

            const token = await getClientAuthToken();
            if (!token) {
                const emptyTokenError = new Error(`Token is empty: ${token}`);
                emptyTokenError.name = 'EmptyTokenError';
                throw emptyTokenError;
            }

            let response = await fetch(this.url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(request.activity),
            });

            const responseJson = await response.json();
            if (responseJson.RefreshCardSerialized) {
                this.onCardDetailsUpdated(JSON.parse(responseJson));
            }
            if (response.ok) {
                let responseToLibrary: ActivityResponse;
                if (responseJson.status === RequestStatus.Completed) {
                    responseToLibrary = {
                        request: request,
                        status: ActivityStatus.Success,
                        content: responseJson.RefreshCardSerialized
                            ? responseJson.RefreshCardSerialized
                            : responseJson.DisplayMessage,
                    };
                } else {
                    responseToLibrary = {
                        request: request,
                        status: ActivityStatus.Failure,
                        content: responseJson.ErrorMessage,
                    };
                }
                return responseToLibrary;
            } else {
                throw response;
            }
        } catch (error) {
            throw error;
        }
    }
}
